// Standalone localStorage bucket for Asset Location Verification.
// Decoupled from the zustand app store so the public token-gated page works
// across refreshes/tabs without persisting the entire app state.

import type { AssetLocationRequest, AssetLocationRecord } from '@/types/postDisbursement';

const KEY = 'astrafin-location-requests-v1';

export interface PublicAssetInfo {
  assetId: string;
  description: string;
}

export interface StoredLocationRequest {
  request: AssetLocationRequest;
  rsId: string;
  customerName: string;
  assets: PublicAssetInfo[];
  locations: AssetLocationRecord[];
}

interface Bucket {
  byToken: Record<string, StoredLocationRequest>;
}

function read(): Bucket {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { byToken: {} };
    return JSON.parse(raw) as Bucket;
  } catch {
    return { byToken: {} };
  }
}

function write(b: Bucket) {
  try { localStorage.setItem(KEY, JSON.stringify(b)); } catch { /* ignore */ }
}

export const locationRequestStore = {
  upsert(entry: StoredLocationRequest) {
    const b = read();
    b.byToken[entry.request.token] = entry;
    write(b);
  },
  updateRequest(token: string, updates: Partial<AssetLocationRequest>) {
    const b = read();
    const e = b.byToken[token];
    if (!e) return;
    e.request = { ...e.request, ...updates };
    write(b);
  },
  addLocations(token: string, records: AssetLocationRecord[]) {
    const b = read();
    const e = b.byToken[token];
    if (!e) return;
    const newIds = new Set(records.map(r => r.assetId));
    e.locations = [
      ...e.locations.map(l => newIds.has(l.assetId) ? { ...l, isCurrent: false } : l),
      ...records,
    ];
    write(b);
  },
  findByToken(token: string): StoredLocationRequest | null {
    return read().byToken[token] || null;
  },
  removeByRequestId(rsId: string, requestId: string) {
    const b = read();
    for (const tok of Object.keys(b.byToken)) {
      const e = b.byToken[tok];
      if (e.rsId === rsId && e.request.id === requestId) delete b.byToken[tok];
    }
    write(b);
  },
};
